import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Register entries surface (T-0031 / E04-F01 CRUD, T-0038 / E04-F07
 * provenance filters). Engagement-scoped under
 * `/api/v1/engagements/:engagement_id/registers/:register_type`.
 *
 * For the scaffold the registers tab targets the
 * `contract_compliance_baseline` register_type as a known default. The
 * full Excel-grid experience (T-0036) layers on top of this same API
 * in a follow-up ticket.
 */

/**
 * The register_type risks and issues are stored under (T-0226 / C2-08).
 * Mirrors RISK_ISSUE_REGISTER_TYPE in apps/api/src/services/registers.ts:
 * the health module composes under it, the health_register_bindings CHECK
 * admits only it, and the RAID list reads it. The Add form, the list and
 * the filter bar all import this one constant so they cannot drift apart
 * again; the entry kind (risk / issue) travels in payload.framework_type.
 */
export const RAID_REGISTER_TYPE = 'risk_issue';

export const RegisterEntrySchema = z.object({
  id: z.string().uuid(),
  register_type: z.string(),
  framework_definition_id: z.string().uuid(),
  status: z.string(),
  payload: z.unknown(),
  origin: z.string(),
  original_ai_confidence: z.number().nullable(),
  latest_reeval_confidence: z.number().nullable(),
  origin_set_at: z.string().nullable(),
  override_tag: z.string().nullable(),
  // C2-56: a finding's domain lives in a column, named by the list read.
  // Optional so a snapshot or an older API answer without them still parses.
  health_domain_id: z.string().nullable().optional(),
  health_domain_name: z.string().nullable().optional(),
  vendor_claim: z.string(),
  avero_verification: z.string(),
  created_by: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type RegisterEntry = z.infer<typeof RegisterEntrySchema>;

export const RegisterListSchema = z.object({
  entries: z.array(RegisterEntrySchema),
  next_cursor: z.string().nullable(),
});
export type RegisterList = z.infer<typeof RegisterListSchema>;

export interface ListRegisterOpts {
  status?: string;
  cursor?: string;
  limit?: number;
  include_deleted?: boolean;
  origin?: string;
  min_confidence?: number;
  max_confidence?: number;
  sort?: string;
}

export function listRegisterEntries(
  engagementId: string,
  registerType: string,
  opts: ListRegisterOpts = {},
): Promise<RegisterList> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/registers/${encodeURIComponent(
      registerType,
    )}`,
    RegisterListSchema,
    { query: opts as Record<string, string | number | boolean | undefined> },
  );
}

export interface UpdateRegisterEntryInput {
  payload?: object;
  status?: string;
  vendor_claim?: string;
  avero_verification?: string;
}

export function updateRegisterEntry(
  engagementId: string,
  registerType: string,
  entryId: string,
  input: UpdateRegisterEntryInput,
  idempotencyKey?: string,
): Promise<RegisterEntry> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/registers/${encodeURIComponent(
      registerType,
    )}/${encodeURIComponent(entryId)}`,
    RegisterEntrySchema,
    {
      method: 'PATCH',
      body: input,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function useRegisterListQuery(
  engagementId: string | undefined,
  registerType: string,
  opts: ListRegisterOpts = {},
) {
  return useQuery({
    queryKey: ['registers', engagementId, registerType, opts] as const,
    queryFn: () => listRegisterEntries(engagementId!, registerType, opts),
    enabled: Boolean(engagementId),
  });
}

// ===========================================================================
// Direct-create register entry (T-0030 / T-0031 — E04-F01 Scenario 3)
// ===========================================================================

/**
 * Direct-create input for `POST /api/v1/engagements/:engagement_id/registers
 * /:register_type`. The payload shape is closed-schema, validated per the
 * referenced framework_definition (T-0030 / `validateRegisterPayload`).
 *
 * Defaults applied server-side when omitted:
 *   - status              → 'open'
 *   - origin              → 'manually_authored'
 *   - vendor_claim        → 'pending'
 *   - avero_verification  → 'unverified'
 *
 * Provenance rules enforced at create (T-0038 / E04-F07):
 *   - origin = manually_authored  ⇒ original_ai_confidence MUST be omitted.
 *   - origin = ai_extracted_confirmed_as_is ⇒ original_ai_confidence
 *     required in [0, 1].
 *   - origin = ai_extracted_edited_by_consultant ⇒ rejected on create
 *     (only reachable through the override-driven flip).
 *
 * The create-form modal (T-0116) ships v1 with `origin='manually_authored'`
 * baked in — the only path the modal exposes.
 */
export interface CreateRegisterEntryInput {
  // Optional — the API self-provisions a per-register-type default
  // framework for manual creates when omitted (Tier 1.2).
  framework_definition_id?: string;
  payload: Record<string, unknown>;
  status?: string;
  origin?: 'manually_authored' | 'ai_extracted_confirmed_as_is';
  original_ai_confidence?: number | null;
  vendor_claim?: string;
  avero_verification?: string;
}

export function createRegisterEntry(
  engagementId: string,
  registerType: string,
  input: CreateRegisterEntryInput,
  idempotencyKey?: string,
): Promise<RegisterEntry> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/registers/${encodeURIComponent(
      registerType,
    )}`,
    RegisterEntrySchema,
    {
      method: 'POST',
      body: input,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function useCreateRegisterEntryMutation(
  engagementId: string | undefined,
  registerType: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      input,
      idempotencyKey,
    }: {
      input: CreateRegisterEntryInput;
      idempotencyKey?: string;
    }) => createRegisterEntry(engagementId!, registerType!, input, idempotencyKey),
    onSuccess: () => {
      if (engagementId && registerType) {
        void qc.invalidateQueries({ queryKey: ['registers', engagementId, registerType] });
      }
    },
  });
}

export function useUpdateRegisterEntryMutation(
  engagementId: string | undefined,
  registerType: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      entryId,
      input,
      idempotencyKey,
    }: {
      entryId: string;
      input: UpdateRegisterEntryInput;
      idempotencyKey?: string;
    }) => updateRegisterEntry(engagementId!, registerType, entryId, input, idempotencyKey),
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['registers', engagementId, registerType] });
      }
    },
    // Optimistic update so the cell value snaps to the new value the moment
    // the user commits the edit; React Query rolls back if the PATCH fails.
    // The optimistic patch only touches the (entryId)-shaped fields we hand
    // in; the rest of the row stays intact via the spread.
    onMutate: async ({ entryId, input }) => {
      if (!engagementId) return { previous: undefined };
      const queryKey = ['registers', engagementId, registerType] as const;
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueriesData<RegisterList>({ queryKey });
      qc.setQueriesData<RegisterList>({ queryKey }, (old) => {
        if (!old) return old;
        return {
          ...old,
          entries: old.entries.map((e) =>
            e.id === entryId
              ? {
                  ...e,
                  ...('status' in input ? { status: input.status! } : {}),
                  ...('vendor_claim' in input ? { vendor_claim: input.vendor_claim! } : {}),
                  ...('avero_verification' in input
                    ? { avero_verification: input.avero_verification! }
                    : {}),
                  ...('payload' in input ? { payload: input.payload } : {}),
                }
              : e,
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      // Rollback all snapshots captured pre-mutation.
      ctx?.previous?.forEach(([key, data]) => {
        if (data) qc.setQueryData(key, data);
      });
    },
  });
}

/**
 * Provenance-enum translation seam — backend `register_entries.origin` →
 * UI-facing provenance vocabulary used by `<ProvenanceChip>` (T-0094).
 *
 * Backend CHECK union (db/prisma/schema.prisma → RegisterEntry):
 *   - ai_extracted_confirmed_as_is        — AI proposed, consultant accepted as-is
 *   - ai_extracted_edited_by_consultant   — AI proposed, consultant edited it
 *   - manually_authored                   — no AI involvement
 *
 * UI vocab (apps/web/src/components/ui/provenance-chip.tsx → Origin):
 *   - ai_proposed       — pending AI proposal not yet confirmed (derived
 *                          from status='proposed' on an AI-extracted row;
 *                          backend keeps the origin until confirm/edit
 *                          decides which terminal value applies)
 *   - ai_confirmed      — accepted as-is
 *   - consultant_edited — edited
 *   - manual            — authored from scratch
 *
 * Rule (ticket §"Origin-enum translation seam"):
 *   ai_extracted_confirmed_as_is        → ai_confirmed
 *   ai_extracted_edited_by_consultant   → consultant_edited
 *   manually_authored                   → manual
 *   status==='proposed' on any ai_extracted_* row → ai_proposed
 *
 * The chip is the load-bearing reuse contract — the register screen, the
 * proposals queue, the audit trail and the snapshot viewer all need to
 * translate identical backend values to identical chip variants. Putting
 * the rule here keeps the chip's prop surface tight (the UI enum) and
 * keeps screens out of the conversion business.
 */
import type { Origin as ProvenanceOrigin } from '@/components/ui/provenance-chip';

export function backendOriginToProvenance(
  backendOrigin: string,
  status?: string,
): ProvenanceOrigin {
  // Pending AI proposal — entry was AI-extracted but the consultant hasn't
  // confirmed/edited yet. Status takes precedence because the ai_extracted_*
  // origins are the consultant's *terminal* decision; an entry in
  // `status='proposed'` is by construction pre-decision.
  if (
    status === 'proposed' &&
    (backendOrigin === 'ai_extracted_confirmed_as_is' ||
      backendOrigin === 'ai_extracted_edited_by_consultant')
  ) {
    return 'ai_proposed';
  }
  switch (backendOrigin) {
    case 'ai_extracted_confirmed_as_is':
      return 'ai_confirmed';
    case 'ai_extracted_edited_by_consultant':
      return 'consultant_edited';
    case 'manually_authored':
    default:
      // Unknown values fall back to `manual` — defensive over UI crash;
      // the audit trail still has the raw backend value if it matters.
      return 'manual';
  }
}

/**
 * `isAiProvenance` — convenience predicate used by the register grid to
 * decide whether to wrap a row in `<AiSurface as="tr">` (any AI-origin row
 * inherits the visual treatment per README §6 + §8). Lifted out of the
 * grid so future surfaces (audit row, proposals queue) hit the same rule.
 */
export function isAiProvenance(origin: ProvenanceOrigin): boolean {
  return origin === 'ai_proposed' || origin === 'ai_confirmed' || origin === 'consultant_edited';
}

/** Fetch one register entry (used by the override host bridge). */
export function getRegisterEntry(
  engagementId: string,
  registerType: string,
  entryId: string,
): Promise<RegisterEntry> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/registers/${encodeURIComponent(
      registerType,
    )}/${encodeURIComponent(entryId)}`,
    RegisterEntrySchema,
  );
}

export function useRegisterEntryQuery(
  engagementId: string | undefined,
  registerType: string | undefined,
  entryId: string | undefined,
) {
  return useQuery({
    queryKey: ['registers', engagementId, 'entry', registerType, entryId] as const,
    queryFn: () => getRegisterEntry(engagementId!, registerType!, entryId!),
    enabled: Boolean(engagementId) && Boolean(registerType) && Boolean(entryId),
  });
}
